import React, { useContext } from 'react';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { Alert, Button, Flex, Form, Input, Popconfirm, Badge, Typography, Card, QRCode } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import {
	useGetBaileysConfigQuery,
	usePostBaileysConfigMutation,
	useDeleteBaileysConfigMutation,
	useRequestBaileysPairingCodeMutation,
	useLazyGetBaileysQRCodeQuery,
	useGetBaileysStatusQuery,
	usePostBaileysAddToGroupsMutation,
	useGetBaileysPartnerInfoQuery
} from 'src/redux/services/centralApi';
import { toast } from 'react-toastify';
import { AuthContext } from 'src/AuthProvider';
import { checkRole } from 'src/common/helpers/checkRole';

const { Text, Title, } = Typography;

interface IPhoneForm {
	phoneNumber: string;
}

export default function ConfigBaileysPhonePage() {
	const { user, } = useContext(AuthContext);
	const isPartnerOrAbove = checkRole('partner', { user, });

	const [ phoneForm, ] = useForm<IPhoneForm>();
	const phoneNumberValue = useWatch('phoneNumber', phoneForm);

	const { data: partnerInfo, } = useGetBaileysPartnerInfoQuery(undefined, {
		skip: !!isPartnerOrAbove,
		pollingInterval: 30000,
	});

	const [ pairingCode, setPairingCode, ] = React.useState<string | null>(null);
	const [ qrCodeValue, setQrCodeValue, ] = React.useState<string | null>(null);

	const [ waitingPairing, setWaitingPairing, ] = React.useState(false);

	const { data: baileysConfig, isError: baileysConfigError, } = useGetBaileysConfigQuery(undefined, {
		pollingInterval: 15000,
	});

	// Quando a config é deletada, o backend retorna 404, mas RTK mantém o cache antigo.
	// Tratamos como undefined quando a query retorna erro.
	const effectiveBaileysConfig = baileysConfigError ? undefined : baileysConfig;
	const { data: baileysStatus, } = useGetBaileysStatusQuery(undefined, {
		pollingInterval: 5000,
		skip: !effectiveBaileysConfig || !waitingPairing,
	});

	const [ postBaileysConfig, postBaileysConfigResult, ] = usePostBaileysConfigMutation();
	const [ deleteBaileysConfig, ] = useDeleteBaileysConfigMutation();
	const [ requestPairingCode, requestPairingCodeResult, ] = useRequestBaileysPairingCodeMutation();
	const [ triggerGetQRCode, getQRCodeResult, ] = useLazyGetBaileysQRCodeQuery();
	const [ postAddToGroups, addToGroupsResult, ] = usePostBaileysAddToGroupsMutation();

	const phoneMask = () => {
		let phoneNumber = phoneNumberValue?.replace(/\D/g, '');

		if (phoneNumber) {
			if (phoneNumber?.length > 11) {
				phoneNumber = phoneNumber?.replace(/(\d{11})(.*)/, '$1');
			} else {
				phoneNumber = phoneNumber
					?.replace(/^(\d{2})(\d)/g, '($1) $2')
					?.replace(/(\d)(\d{4})$/, '$1-$2');
			}
			phoneForm.setFieldValue('phoneNumber', phoneNumber);
			phoneNumber?.length &&
			phoneForm.validateFields([ 'phoneNumber', ]);
		}
	};

	const onPhoneFormFinish = (data: IPhoneForm) => {
		const cleanPhone = data.phoneNumber.replace(/\D/g, '');
		postBaileysConfig({ phoneNumber: cleanPhone, })
			.unwrap()
			.then(() => {
				toast.success('Número registrado com sucesso!');
				setPairingCode(null);
			})
			.catch((reason) => {
				if (reason.status === 409) {
					toast.error('Já existe um número configurado!');
				} else {
					toast.error('Erro ao registrar número!');
				}
			});
	};

	const handleRequestPairingCode = () => {
		if (!effectiveBaileysConfig?.phoneNumber) return;
		requestPairingCode({ phoneNumber: effectiveBaileysConfig.phoneNumber, })
			.unwrap()
			.then((result) => {
				setPairingCode(result.code);
				setQrCodeValue(null);
				setWaitingPairing(true);
				toast.success('Código de pareamento gerado! Expira em 1 minuto.');
			})
			.catch(() => {
				toast.error(
					'Erro ao solicitar código de pareamento. '
					+ 'Aguarde alguns segundos e tente novamente.'
				);
			});
	};

	const handleGetQRCode = () => {
		triggerGetQRCode()
			.unwrap()
			.then((result) => {
				if (result.qrCode) {
					setQrCodeValue(result.qrCode);
					setPairingCode(null);
					setWaitingPairing(true);
				} else {
					toast.info(
						'QR Code ainda não disponível. '
						+ 'Tente novamente em alguns segundos.'
					);
				}
			})
			.catch(() => {
				toast.error(
					'QR Code ainda não disponível. '
					+ 'Tente novamente em alguns segundos.'
				);
			});
	};

	const handleAddToGroups = () => {
		postAddToGroups()
			.unwrap()
			.then(() => {
				toast.success('Número adicionado a todos os grupos com sucesso!');
			})
			.catch(() => {
				toast.error('Erro ao adicionar número aos grupos.');
			});
	};

	React.useEffect(() => {
		if (effectiveBaileysConfig?.phoneNumber) {
			const phone = effectiveBaileysConfig.phoneNumber;
			const formatted = phone.length === 11
				? phone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
				: phone;
			phoneForm.setFieldValue('phoneNumber', formatted);
		}
	}, [ effectiveBaileysConfig, ]);

	React.useEffect(() => {
		phoneMask();
	}, [ phoneNumberValue, ]);

	const isConnected = (waitingPairing ? baileysStatus?.isConnected : effectiveBaileysConfig?.isConnected) ?? false;

	// Para o polling quando conectar
	React.useEffect(() => {
		if (isConnected && waitingPairing) {
			setWaitingPairing(false);
			setPairingCode(null);
			setQrCodeValue(null);
			toast.success('WhatsApp conectado com sucesso!');
		}
	}, [ isConnected, ]);

	// Usuário sem role de parceiro: exibe banner informativo com o número do parceiro
	if (!isPartnerOrAbove) {
		const formatPhone = (phone: string | null | undefined) => {
			if (!phone) return null;
			const d = phone.replace(/\D/g, '');
			if (d.length === 11) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
			if (d.length === 10) return d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
			return phone;
		};

		return (
			<DashboardTemplate pageTitle="Número WhatsApp (Baileys)">
				<Flex vertical gap={16}>
					{partnerInfo?.phoneNumber ? (
						<Alert
							type="info"
							showIcon
							message="Número configurado pelo parceiro"
							description={
								<Flex vertical gap={4}>
									<span>
										<strong>Número:</strong> {formatPhone(partnerInfo.phoneNumber)}
									</span>
									<span>
										<strong>Status:</strong>{' '}
										<Badge
											status={partnerInfo.isConnected ? 'success' : 'error'}
											text={partnerInfo.isConnected ? 'Conectado' : 'Desconectado'}
										/>
									</span>
									<span style={{ marginTop: 4, color: '#595959', }}>
										As mensagens desta organização serão enviadas por esse número.
										A configuração é gerenciada pelo parceiro.
									</span>
								</Flex>
							}
						/>
					) : partnerInfo?.hasPartner ? (
						<Alert
							type="warning"
							showIcon
							message="Nenhum número configurado pelo parceiro"
							description="O parceiro ainda não configurou um número WhatsApp via Baileys. As mensagens serão enviadas pela Z-API enquanto isso."
						/>
					) : (
						<Alert
							type="info"
							showIcon
							message="Configuração não disponível"
							description="Esta organização não possui um parceiro associado. Entre em contato com o suporte."
						/>
					)}
				</Flex>
			</DashboardTemplate>
		);
	}

	return (
		<DashboardTemplate pageTitle="Configurar Número de Telefone (Baileys)">
			<Flex vertical gap={24}>
				{/* Status da conexão */}
				{effectiveBaileysConfig && (
					<Card size="small" title="Status da Conexão">
						<Flex align="center" gap={12}>
							<Badge
								status={isConnected ? 'success' : 'error'}
								text={
									isConnected
										? `Conectado - ${baileysStatus?.phoneNumber ?? effectiveBaileysConfig.phoneNumber}`
										: `Desconectado - ${effectiveBaileysConfig.phoneNumber}`
								}
							/>
						</Flex>
					</Card>
				)}

				{/* Formulário de registro do número */}
				<Card size="small" title="Registrar Número">
					<Form
						onFinish={onPhoneFormFinish}
						layout="vertical"
						form={phoneForm}
					>
						<Form.Item
							required
							hasFeedback
							tooltip={<>Insira o número do WhatsApp que será usado para enviar mensagens via Baileys.</>}
							label="Número do WhatsApp"
							name="phoneNumber"
							rules={[
								{
									min: 2,
									message: 'Insira um número válido.',
								},
								{
									validator: (_, value) => {
										if (!value) return Promise.reject();
										if (value.match(/\(\d{2}\) 9\d{4}-\d{4}/)) {
											return Promise.resolve();
										}
										return Promise.reject();
									},
									message: 'Insira um número válido. Ex: (11) 91234-5678',
								},
							]}
						>
							<Input placeholder="(11) 91234-5678" disabled={!!effectiveBaileysConfig} />
						</Form.Item>
						<Flex gap={10} justify="space-between">
							<Form.Item>
								{!effectiveBaileysConfig && (
									<Button
										loading={postBaileysConfigResult.isLoading}
										type="primary"
										htmlType="submit"
									>
										Registrar Número
									</Button>
								)}
							</Form.Item>
							<Form.Item>
								{!!effectiveBaileysConfig && (
									<Popconfirm
										title="Tem certeza que deseja remover o número configurado?"
										description="A sessão do Baileys será desconectada."
										onCancel={() =>
											toast.info('Operação cancelada!', {
												autoClose: 1000,
											})
										}
										onConfirm={() => {
											deleteBaileysConfig()
												.unwrap()
												.then(() => {
													phoneForm.resetFields();
													setPairingCode(null);
													setQrCodeValue(null);
													setWaitingPairing(false);
													toast.success('Configuração removida com sucesso!');
												})
												.catch(() => {
													toast.error('Erro ao remover configuração!');
												});
										}}
									>
										<Button type="primary" danger>
											Remover Número
										</Button>
									</Popconfirm>
								)}
							</Form.Item>
						</Flex>
					</Form>
				</Card>

				{/* Pareamento - só aparece se há config mas não está conectado */}
				{effectiveBaileysConfig && !isConnected && (
					<Card size="small" title="Parear Dispositivo">
						<Flex vertical gap={16}>
							<Text>
								Para conectar o WhatsApp, use uma das opções abaixo.
								Abra o WhatsApp no celular &gt; Dispositivos conectados
								&gt; Conectar dispositivo.
							</Text>
							<Flex gap={12} wrap="wrap">
								<Button
									type="primary"
									onClick={handleRequestPairingCode}
									loading={requestPairingCodeResult.isLoading}
								>
									Gerar Código de Pareamento
								</Button>
								<Button
									onClick={handleGetQRCode}
									loading={getQRCodeResult.isFetching}
								>
									Obter QR Code
								</Button>
							</Flex>
							{pairingCode && (
								<Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f', }}>
									<Flex vertical align="center" gap={8}>
										<Text>Insira este código no WhatsApp:</Text>
										<Title level={2} style={{ margin: 0, letterSpacing: 4, }}>
											{pairingCode}
										</Title>
										<Text type="secondary">O código expira em 1 minuto.</Text>
									</Flex>
								</Card>
							)}
							{qrCodeValue && (
								<Card size="small">
									<Flex vertical align="center" gap={8}>
										<Text>
											Escaneie o QR Code com o WhatsApp:
										</Text>
										<QRCode value={qrCodeValue} size={256} />
									</Flex>
								</Card>
							)}
						</Flex>
					</Card>
				)}

				{/* Adicionar a grupos - só aparece se está conectado */}
				{effectiveBaileysConfig && isConnected && (
					<Card size="small" title="Adicionar aos Grupos">
						<Flex vertical gap={12}>
							<Text>
								Adicione o número configurado a todos os grupos de WhatsApp existentes
								e promova-o como administrador. Isso é necessário para que o envio de mensagens
								funcione via Baileys.
							</Text>
							<Button
								type="primary"
								onClick={handleAddToGroups}
								loading={addToGroupsResult.isLoading}
							>
								Adicionar a Todos os Grupos
							</Button>
						</Flex>
					</Card>
				)}
			</Flex>
		</DashboardTemplate>
	);
}
